import { useCallback, useEffect, useRef, useState } from "react";
import { getAgentRoutingSettings, saveAgentRoutingSettings } from "@/api/agentRoutingApi";
import { getConversationGraph } from "@/api/conversationsApi";
import { previewNextRoundRobinAssignee } from "@/lib/roundRobin";

function Toggle({ on, onToggle, id }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={on}
      id={id}
      onClick={onToggle}
      className={`inline-flex h-7 w-12 shrink-0 cursor-pointer items-center rounded-full p-1 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-teal-500 focus-visible:ring-offset-2 ${
        on ? "ui-toggle-track-on" : "ui-toggle-track-off"
      }`}
    >
      <span
        className={`pointer-events-none inline-block h-5 w-5 rounded-full bg-white shadow-md ring-1 ring-slate-200/70 transition-transform duration-200 ease-out ${
          on ? "translate-x-5" : "translate-x-0"
        }`}
      />
    </button>
  );
}

/** Custom listbox for rotation cursor — native `<option>` cannot be styled meaningfully. */
function RotationPointerPicker({ agentOrderIds, agentById, value, onChange, id }) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const selectedId = agentOrderIds[value];
  const selectedAgent = selectedId ? agentById[selectedId] : null;

  return (
    <div ref={rootRef} className="relative max-w-md">
      <button
        type="button"
        id={id}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={`${id}-listbox`}
        onClick={() => setOpen((o) => !o)}
        className="group flex w-full items-center gap-3 rounded-xl border border-slate-200/90 bg-white px-3.5 py-3 text-left shadow-sm ring-1 ring-slate-200/40 transition hover:border-teal-200/80 hover:shadow-md hover:ring-teal-200/30 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-teal-500 focus-visible:ring-offset-2"
      >
        <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-teal-500 to-cyan-600 text-sm font-bold text-white shadow-inner ring-2 ring-white/30">
          {value + 1}
        </span>
        <span className="min-w-0 flex-1">
          <span className="block text-[10px] font-bold uppercase tracking-widest text-slate-400">Active pointer</span>
          <span className="mt-0.5 block truncate text-sm font-semibold text-slate-900">{selectedAgent?.name ?? "—"}</span>
          <span className="mt-0.5 block text-[11px] text-slate-500">Position {value + 1} in queue</span>
        </span>
        <i
          className={`ri-arrow-down-s-line shrink-0 text-xl text-slate-400 transition-transform duration-200 ${open ? "rotate-180 text-teal-600" : "group-hover:text-slate-600"}`}
          aria-hidden
        />
      </button>

      {open && (
        <ul
          id={`${id}-listbox`}
          role="listbox"
          aria-label="Choose rotation pointer position"
          className="absolute left-0 right-0 top-full z-20 mt-2 max-h-72 overflow-auto rounded-xl border border-slate-200/90 bg-white py-1.5 shadow-xl shadow-slate-900/10 ring-1 ring-slate-200/60"
        >
          {agentOrderIds.map((aid, i) => {
            const agent = agentById[aid];
            const selected = i === value;
            return (
              <li key={aid} role="presentation">
                <button
                  type="button"
                  role="option"
                  aria-selected={selected}
                  onClick={() => {
                    onChange(i);
                    setOpen(false);
                  }}
                  className={`flex w-full items-center gap-3 px-3 py-2.5 text-left transition-colors ${
                    selected
                      ? "bg-gradient-to-r from-teal-50/90 to-cyan-50/50"
                      : "hover:bg-slate-50"
                  }`}
                >
                  <span
                    className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-lg text-xs font-bold tabular-nums shadow-sm ${
                      selected
                        ? "bg-gradient-to-br from-teal-500 to-cyan-600 text-white ring-2 ring-teal-300/50"
                        : "bg-slate-100 text-slate-600 ring-1 ring-slate-200/80"
                    }`}
                  >
                    {i + 1}
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className={`block truncate text-sm font-semibold ${selected ? "text-teal-950" : "text-slate-900"}`}>
                      {agent?.name ?? aid}
                    </span>
                    <span className={`mt-0.5 block text-[11px] ${selected ? "text-teal-800/80" : "text-slate-500"}`}>
                      {selected ? "Next assignment picks after this slot" : `Queue slot ${i + 1}`}
                    </span>
                  </span>
                  {selected && (
                    <span className="shrink-0 rounded-full bg-teal-600/10 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-teal-800 ring-1 ring-teal-300/40">
                      Current
                    </span>
                  )}
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

export default function RoutingSettingsSection() {
  const [loading, setLoading] = useState(true);
  const [saved, setSaved] = useState(false);
  const [roundRobinEnabled, setRoundRobinEnabled] = useState(false);
  const [agents, setAgents] = useState(/** @type {Array<{ id: string, name: string, minConcurrentChats: number, maxConcurrentChats: number }> | null} */ (null));
  const [agentOrderIds, setAgentOrderIds] = useState([]);
  const [roundRobinCursor, setRoundRobinCursor] = useState(0);
  const [previewNext, setPreviewNext] = useState(/** @type {string | null} */ (null));

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await getAgentRoutingSettings();
      setRoundRobinEnabled(data.roundRobinEnabled);
      setAgents(data.agents.map((a) => ({ ...a })));
      setAgentOrderIds([...data.agentOrderIds]);
      setRoundRobinCursor(data.roundRobinCursor);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    const onEvt = () => void load();
    window.addEventListener("agent-routing-changed", onEvt);
    return () => window.removeEventListener("agent-routing-changed", onEvt);
  }, [load]);

  useEffect(() => {
    if (!agents?.length || !agentOrderIds.length) {
      setPreviewNext(null);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const { data: graph } = await getConversationGraph();
        const list = Array.isArray(graph) ? graph : [];
        const next = previewNextRoundRobinAssignee(list, {
          agents,
          agentOrderIds,
          roundRobinCursor,
          roundRobinEnabled,
        });
        if (!cancelled) setPreviewNext(next);
      } catch {
        if (!cancelled) setPreviewNext(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [agents, agentOrderIds, roundRobinCursor, roundRobinEnabled]);

  const agentById = agents ? Object.fromEntries(agents.map((a) => [a.id, a])) : {};

  const moveOrder = (index, dir) => {
    setAgentOrderIds((prev) => {
      const next = [...prev];
      const j = index + dir;
      if (j < 0 || j >= next.length) return prev;
      const t = next[index];
      next[index] = next[j];
      next[j] = t;
      return next;
    });
  };

  const updateAgentField = (id, field, raw) => {
    setAgents((prev) => {
      if (!prev) return prev;
      return prev.map((a) => {
        if (a.id !== id) return a;
        const n = parseInt(String(raw), 10);
        const val = Number.isFinite(n) ? Math.max(0, n) : a[field];
        return { ...a, [field]: val };
      });
    });
  };

  const normalizeAgents = () => {
    if (!agents) return [];
    return agents.map((a) => {
      let min = Math.max(0, parseInt(String(a.minConcurrentChats), 10) || 0);
      let max = parseInt(String(a.maxConcurrentChats), 10);
      if (!Number.isFinite(max) || max < 1) max = 8;
      if (max < min) max = min;
      return { ...a, minConcurrentChats: min, maxConcurrentChats: max };
    });
  };

  const handleSave = async () => {
    setSaved(false);
    const normalized = normalizeAgents();
    await saveAgentRoutingSettings({
      roundRobinEnabled,
      agents: normalized,
      agentOrderIds,
      roundRobinCursor,
    });
    setAgents(normalized);
    setSaved(true);
    window.setTimeout(() => setSaved(false), 2500);
  };

  if (loading || !agents) {
    return (
      <div className="mx-auto max-w-6xl space-y-6">
        <p className="text-sm text-slate-500">Loading routing…</p>
      </div>
    );
  }

  if (!agents.length) {
    return (
      <div className="mx-auto max-w-6xl space-y-6">
        <div>
          <h3 className="text-xl font-semibold tracking-tight text-slate-900">Routing & capacity</h3>
          <p className="mt-1 text-sm text-slate-500">
            No human agents are registered for this workspace yet. Invite agents under{" "}
            <span className="font-medium text-slate-700">Users</span>, then return here to configure round-robin and
            concurrency limits.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-6xl space-y-6">
      <div>
        <h3 className="text-xl font-semibold tracking-tight text-slate-900">Routing & capacity</h3>
        <p className="mt-1 text-sm text-slate-500">
          Cap concurrent open chats per agent and define round-robin order for fair distribution. Transfers in the chat header use this agent list.
        </p>
      </div>

      <div className="ui-card-static space-y-6 !p-6 sm:!p-8">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <p className="text-sm font-semibold text-slate-900">Round robin for new assignments</p>
            <p className="mt-1 text-xs text-slate-500">
              When enabled, new chats are offered to the next Online (Available) agent in rotation who is under their
              maximum. Offline / Away agents are skipped automatically.
            </p>
          </div>
          <Toggle id="rr-toggle" on={roundRobinEnabled} onToggle={() => setRoundRobinEnabled((v) => !v)} />
        </div>

        <div className="rounded-xl border border-slate-100 bg-slate-50/80 px-4 py-3 text-sm text-slate-700">
          <span className="font-semibold text-slate-800">Preview — next in rotation: </span>
          {roundRobinEnabled ? previewNext ?? <span className="text-slate-500">No slot (all offline or at capacity)</span> : <span className="text-slate-500">Round robin off</span>}
        </div>

        <div>
          <p className="mb-2 text-sm font-semibold text-slate-900">Round robin order</p>
          <p className="mb-3 text-xs text-slate-500">Top = first in rotation after the cursor. Use arrows to map equal division order.</p>
          <ul className="space-y-2">
            {agentOrderIds.map((id, index) => {
              const a = agentById[id];
              if (!a) return null;
              return (
                <li key={id} className="flex items-center gap-2 rounded-xl border border-slate-100 bg-white px-3 py-2 shadow-sm">
                  <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-slate-100 text-xs font-bold text-slate-500">{index + 1}</span>
                  <span className="min-w-0 flex-1 truncate text-sm font-medium text-slate-900">{a.name}</span>
                  <span
                    className={`shrink-0 rounded-md px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${
                      a.isOnline
                        ? "bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200"
                        : "bg-slate-100 text-slate-500 ring-1 ring-slate-200"
                    }`}
                  >
                    {a.isOnline ? "Online" : "Offline"}
                  </span>
                  <div className="flex shrink-0 gap-1">
                    <button type="button" className="ui-btn-icon !h-8 !w-8" aria-label="Move up" onClick={() => moveOrder(index, -1)} disabled={index === 0}>
                      <i className="ri-arrow-up-line" />
                    </button>
                    <button type="button" className="ui-btn-icon !h-8 !w-8" aria-label="Move down" onClick={() => moveOrder(index, 1)} disabled={index === agentOrderIds.length - 1}>
                      <i className="ri-arrow-down-line" />
                    </button>
                  </div>
                </li>
              );
            })}
          </ul>
        </div>

        <div>
          <p className="mb-3 text-sm font-semibold text-slate-900">Per-agent chat limits (open conversations)</p>
          <div className="overflow-x-auto rounded-xl border border-slate-100">
            <table className="w-full min-w-[420px] text-left text-sm">
              <thead>
                <tr className="border-b border-slate-100 bg-slate-50/80">
                  {["Agent", "Min concurrent", "Max concurrent"].map((h) => (
                    <th key={h} className="px-4 py-3 text-[10px] font-bold uppercase tracking-widest text-slate-400">
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {agents.map((a) => (
                  <tr key={a.id}>
                    <td className="px-4 py-3 font-medium text-slate-900">{a.name}</td>
                    <td className="px-4 py-3">
                      <input
                        type="number"
                        min={0}
                        className="ui-field !py-2 !text-sm"
                        value={a.minConcurrentChats}
                        onChange={(e) => updateAgentField(a.id, "minConcurrentChats", e.target.value)}
                        aria-label={`${a.name} minimum concurrent chats`}
                      />
                    </td>
                    <td className="px-4 py-3">
                      <input
                        type="number"
                        min={1}
                        className="ui-field !py-2 !text-sm"
                        value={a.maxConcurrentChats}
                        onChange={(e) => updateAgentField(a.id, "maxConcurrentChats", e.target.value)}
                        aria-label={`${a.name} maximum concurrent chats`}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="mt-2 text-xs text-slate-500">
            Maximum must be ≥ minimum. Values are saved to workspace routing settings on the server.
          </p>
        </div>

        <div>
          <label htmlFor="rr-cursor" className="mb-2 block text-sm font-semibold text-slate-800">
            Rotation pointer (start index)
          </label>
          <RotationPointerPicker
            id="rr-cursor"
            agentOrderIds={agentOrderIds}
            agentById={agentById}
            value={roundRobinCursor}
            onChange={setRoundRobinCursor}
          />
          <p className="mt-2 text-xs text-slate-500">The next round-robin pick begins after this position in the ordered list.</p>
        </div>

        <div className="flex flex-wrap items-center gap-3 border-t border-slate-100 pt-6">
          <button type="button" className="ui-btn-primary" onClick={() => void handleSave()}>
            Save routing settings
          </button>
          {saved && <span className="text-sm font-medium text-emerald-700">Saved.</span>}
        </div>
      </div>
    </div>
  );
}
